import { useEffect, type ReactNode } from "react";
import { useMe } from "../api/queries.js";
import {
  PORTAL_REDIRECT_MESSAGE,
  PORTAL_UNCONFIGURED_MESSAGE,
  staffSurfaceDecision,
} from "./staffSurface.js";

/**
 * Client-firm staff — sits just inside AuthGate. Reads `/me` once (the same cached query the
 * screens use) and, for a person whose firm works in the client portal, shows one line and sends
 * them to the portal instead of rendering any console screen. Everyone else gets the console.
 *
 * This is the courtesy half. The engine refuses such a person every console route on its own
 * (`staff_surface_is_portal`), so if `/me` cannot be read we fall through to the console rather
 * than lock every operator out: nothing a portal user could reach from there would answer.
 */
export function StaffSurfaceGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const decision = me.data ? staffSurfaceDecision(me.data) : null;

  useEffect(() => {
    if (decision?.kind === "portal") window.location.replace(decision.url);
  }, [decision?.kind, decision?.kind === "portal" ? decision.url : null]);

  if (me.isPending) return <div className="auth-loading">Checking your session...</div>;
  if (decision?.kind === "portal") {
    return <div className="auth-loading" role="status">{PORTAL_REDIRECT_MESSAGE}</div>;
  }
  if (decision?.kind === "portal-unconfigured") {
    return <div className="auth-loading" role="status">{PORTAL_UNCONFIGURED_MESSAGE}</div>;
  }
  return <>{children}</>;
}
